<template>
  <div class="textbook-render" 
    :class="{
      'no-title-cover': hideTitle && (!cover || cover.hide),
      'just-cover': cover && cover.src && !cover.hide && hideTitle && noContent,
    }" 
    ref="renderRef"
  >
    <div class="cover" v-if="cover && cover.src && !cover.hide" :style="{ 'padding-bottom': cover.scale * 100 + '%' }">
      <div class="cover-content" :style="{ 'background-image': `url(${cover.src})` }"></div>
    </div>
    <div class="title" 
      :class="{
        'phone': isPhone,
        'no-cover': !cover || cover.hide,
      }" 
      v-if="!hideTitle"
    >{{ title || '测试标题' }}</div>
    <MarkContainer 
      class="textbook-render-container" 
      :class="{
        'hide-title': hideTitle,
        'phone': isPhone,
      }" 
      v-if="initSuccess && userInfo && (!hideTitle || !noContent)" 
      ref="markContainerRef" 
      :content="content"
      :interactionConfigs="interactionConfigs" 
      :iframeConfigs="iframeConfigs" 
      :appConfigs="appConfigs"
      :defaultStyle="globalStyle" 
      :inductionIndex="inductionIndex"
      :studentInfo="userInfo" 
      :guid="textbookData.PK_InductionGuid"
      :appList="textbookData.children" 
      :classId="currentClass.PK_ClassGuid" 
      :nodeIdMap="nodeIdMap"
      @noteUpdated="$emit('noteUpdated')"
      @cardUpdated="$emit('cardUpdated')"
      @search="keyword => search(keyword)"
      @recordJump="data => $emit('recordJump', data)"
      @jumpTargetNode="$event => $emit('jumpTargetNode', $event)"
    />
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import debounce from 'lodash/debounce'
import MarkContainer from './MarkContainer'

export default {
  name: 'textbook-render',
  components: {
    MarkContainer,
  },
  props: {
    textbookData: {
      type: Object,
      required: true,
    },
    isPhone: {
      type: Boolean,
      default: false,
    },
    inductionIndex: {
      type: Number,
      default: -1,
    },
    nodeIdMap: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      title: '',
      content: '',
      hideTitle: false,
      interactionConfigs: {},
      iframeConfigs: {},
      appConfigs: {},
      defaultStyle: {
        fontSize: '16px',
      },
      cover: {
        hide: true,
      },
      initSuccess: false,
      anchors: [],
      scrollBoxRef: null,
      activeAnchor: '',
    }
  },
  computed: {
    ...mapState({
      userInfo: state => state.userInfo,
      currentClass: state => state.currentClass,
      readSetting: state => state.readSetting,
    }),
    globalStyle() {
      if (this.readSetting) {
        if (this.readSetting.fontsize) {
          return {
            ...this.defaultStyle,
            fontSize: `${this.readSetting.fontsize}px`,
          }
        }
      }
      return this.defaultStyle
    },
    noContent() {
      return !this.content.replace(/<\/?p\b[^>]*>/g, '')
    },
  },
  watch: {
    activeAnchor() {
      const index = this.anchors.findIndex(anchor => anchor.id === this.activeAnchor)
      this.$emit('updateReadAnchor', {
        inductionId: this.textbookData.PK_InductionGuid,
        anchor: this.activeAnchor,
        count: this.anchors.length,
        index: index + 1,
      })
    },

    readSetting: {
      handler(newSetting, oldSetting) {
        if (newSetting && newSetting.fontsize) {
          const scale = newSetting.fontsize / ((oldSetting && oldSetting.fontsize) || parseInt(this.defaultStyle.fontSize))
          const renderRef = this.$refs.renderRef

          const elementsWithFontSize = renderRef.querySelectorAll('[style*="font-size"]')
          for (const element of elementsWithFontSize) {
            const currentFontSize = parseInt(window.getComputedStyle(element).fontSize)
            element.style.fontSize = (currentFontSize * scale) + 'px'
          }
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.title = this.textbookData.InductionName
    const {
      content,
      interactionConfigs,
      iframeConfigs,
      appConfigs,
      defaultStyle,
      hideTitle,
      cover,
    } = JSON.parse(this.textbookData.TextbookContent)

    this.content = content
    this.hideTitle = hideTitle || false
    this.interactionConfigs = interactionConfigs
    this.iframeConfigs = iframeConfigs
    this.appConfigs = appConfigs
    if (defaultStyle) this.defaultStyle = defaultStyle
    if (cover) this.cover = cover

    this.initSuccess = true

    this.$nextTick(() => {
      this.scrollBoxRef = document.querySelector('.scroll-box')
      this.initAnchors()
      this.scrollEventListener()
      this.scrollBoxRef.addEventListener('scroll', this.scrollEventListener)
    })
  },
  beforeUnmount() {
    if (this.scrollBoxRef) this.scrollBoxRef.removeEventListener('scroll', this.scrollEventListener)
  },
  methods: {
    ...mapMutations({
      setPilotNav: 'pilotNavOpen',
      setSearchKeyword: 'setSearchKeyword',
    }),

    deleteNote(note) {
      this.$refs.markContainerRef.deleteNote(note)
    },
    deleteCard(uid) {
      this.$refs.markContainerRef.deleteCard(uid)
    },
    updateCard(uid, props) {
      this.$refs.markContainerRef.updateCard(uid, props)
    },

    search(keyword) {
      this.setSearchKeyword(keyword)
      this.setPilotNav('search')
    },

    initAnchors() {
      const anchorRefs = document.querySelectorAll('.render-content > *')
      const anchors = []
      for (const anchorRef of anchorRefs) {
        let id = anchorRef.dataset?.id
        if (!id) {
          const idNode = anchorRef.querySelector('[data-id]')
          if (idNode) id = idNode.dataset.id
        }
        if (!id) continue

        anchors.push({
          ref: anchorRef,
          id,
        })
      }
      this.anchors = anchors
    },

    scrollEventListener: debounce(function () {
      const wrapRect = this.scrollBoxRef.getBoundingClientRect()

      const inViewportAnchors = []
      const overAnchors = []

      for (const anchor of this.anchors) {
        const anchorRect = anchor.ref.getBoundingClientRect()

        if (anchorRect.bottom <= wrapRect.top) overAnchors.push(anchor.id)
        if (
          anchorRect.top >= wrapRect.top - 5 &&
          anchorRect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
        ) {
          inViewportAnchors.push(anchor.id)
        }
      }
      if(this.anchors.length) {
        this.activeAnchor = inViewportAnchors[inViewportAnchors.length - 1] || overAnchors[overAnchors.length - 1] || this.anchors[0].id
      }
    }, 3000, { trailing: true }),
  },
}
</script>

<style lang="scss" scoped>
.textbook-render {
  display: flex;
  flex-direction: column;

  &.just-cover {
    min-height: 0;
    height: auto;

    .cover {
      border-radius: 14px;

      .cover-content {
        border-radius: 14px;
      }
    }
  }
}

.cover {
  position: relative;
  height: 0;
  background-color: rgba($color: #666, $alpha: .05);
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;

  .cover-content {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-top-left-radius: 14px;
    border-top-right-radius: 14px;
  }
}

.title {
  line-height: 1.2;
  padding: 15px 0;
  margin: 40px 70px 0;
  font-size: 36px;
  text-align: center;
  font-weight: 700;

  &.phone {
    padding: 10px 0;
    margin: 20px 30px 0;

    &.no-cover {
      margin-top: 70px;
    }
  }
}

.textbook-render-container {
  padding: 40px 70px 70px;

  &.hide-title {
    padding-top: 70px;
  }

  &.phone {
    padding: 20px 30px 30px;

    &.hide-title {
      padding-top: 30px;
    }
  }
}
</style>

<style lang="scss">
.tippy-box[data-theme~='tooltip'] {
  background-color: #333;
  color: #fff;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 12px;
}

.search-highlight {
  background-color: yellow;
}

.no-title-cover {
  .render-content {
    &>.box-view[data-margin='auto']:first-child {
      margin-top: -70px;
      border-top-left-radius: 14px;
      border-top-right-radius: 14px;
      overflow: hidden;
    }
  }

  .phone .render-content {
    &>.box-view[data-margin='auto']:first-child {
      margin-top: -30px;
    }
  }
}
</style>